import { yup } from '@jbc-year-end-adj/common/yup';
import { HousingLoan, HousingLoanDetail, DeductionClassification, SpecialDeduction } from './query';
import { FileType } from '@jbc-year-end-adj/types';
import { dateFormFormat, amountFormat } from '@jbc-year-end-adj/common/utils/formatter';
import { fileSchemaFormat } from '@jbc-year-end-adj/2024/utils/formatter';

export type Schema = {
  housingLoanDeductionApplicableCount: string;
  housingLoanDeductionApplicableAmount: string;
  image?: FileType | File | null;
  certificateImage?: FileType | File | null;
  firstDetail: HousingLoanDetailSchema;
  secondDetail?: HousingLoanDetailSchema;
};

export type HousingLoanDetailSchema = {
  residenceStartOn: string;
  deductionClassification: DeductionClassification;
  specialDeduction: SpecialDeduction;
  yearEndBalance: string;
};

export const schema = (imageRequired: boolean) => {
  return yup.object({
    housingLoanDeductionApplicableCount: yup
      .mixed<'1' | '2'>()
      .required()
      .label('住宅借入金等 特別控除適用数'),
    housingLoanDeductionApplicableAmount: yup
      .string()
      .required()
      .positiveAmountFormat()
      .maxAmount(999999999)
      .label('住宅借入金等特別控除額'),
    image: imageRequired
      ? yup
          .mixed<FileType | File>()
          .fileSize()
          .required()
          .label('住宅借入金等特別控除申告書の画像')
      : yup
          .mixed<FileType | File>()
          .fileSize()
          .nullable()
          .label('住宅借入金等特別控除申告書の画像'),
    certificateImage: imageRequired
      ? yup
          .mixed<FileType | File>()
          .fileSize()
          .required()
          .label('住宅ローンの年末残高証明書の画像')
      : yup
          .mixed<FileType | File>()
          .fileSize()
          .nullable()
          .label('住宅ローンの年末残高証明書の画像'),
    firstDetail: yup
      .object({
        residenceStartOn: yup
          .string()
          .required()
          .dateFormat()
          .label('居住開始年月日（1回目）'),
        deductionClassification: yup
          .mixed<DeductionClassification>()
          .required()
          .label('住宅借入金等特別控除区分（1回目）'),
        specialDeduction: yup
          .mixed<SpecialDeduction>()
          .required()
          .label('住宅借入金等特別控除区分（1回目）　特定'),
        yearEndBalance: yup
          .string()
          .required()
          .positiveAmountFormat()
          .maxAmount(999999999)
          .label('住宅借入金等 年末残高（1回目）')
      })
      .required(),
    secondDetail: yup
      .object({
        residenceStartOn: yup
          .string()
          .test('hasSecondDetail', ({ label }) => `${label}を入力してください`, secondDetailRequired)
          .dateFormat()
          .label('居住開始年月日（2回目）'),
        deductionClassification: yup
          .mixed<DeductionClassification>()
          .test('hasSecondDetail', ({ label }) => `${label}を入力してください`, secondDetailRequired)
          .label(`住宅借入金等特別控除区分（2回目）`),
        specialDeduction: yup
          .mixed<SpecialDeduction>()
          .test('hasSecondDetail', ({ label }) => `${label}を入力してください`, secondDetailRequired)
          .label('住宅借入金等特別控除区分（2回目）　特定'),
        yearEndBalance: yup
          .string()
          .test('hasSecondDetail', ({ label }) => `${label}を入力してください`, secondDetailRequired)
          .positiveAmountFormat()
          .maxAmount(999999999)
          .label('住宅借入金等 年末残高（2回目）')
      })
      .nullable()
  });
};

const secondDetailRequired: yup.TestFunction = (_, context) => {
  if (context.from?.[1]?.value.housingLoanDeductionApplicableCount === '1') return true;
  return context.originalValue !== undefined && context.originalValue !== '';
};

export const generateDefaultValues = (housingLoan: HousingLoan | undefined): Schema => {
  const firstDetail = housingLoan?.housingLoanDetails?.find(detail => detail.no === 1);
  const secondDetail = housingLoan?.housingLoanDetails?.find(detail => detail.no === 2);

  return {
    housingLoanDeductionApplicableCount: housingLoan?.housingLoanDeductionApplicableCount || '1',
    housingLoanDeductionApplicableAmount: amountFormat(housingLoan?.housingLoanDeductionApplicableAmount),
    image: fileSchemaFormat(housingLoan?.image),
    certificateImage: fileSchemaFormat(housingLoan?.certificateImage),
    firstDetail: housingLoanDetailDefaultValues(firstDetail),
    secondDetail: housingLoanDetailDefaultValues(secondDetail)
  };
};

const housingLoanDetailDefaultValues = (housingLoanDetail: HousingLoanDetail | undefined): HousingLoanDetailSchema => {
  return {
    residenceStartOn: dateFormFormat(housingLoanDetail?.residenceStartOn, 'L'),
    deductionClassification: housingLoanDetail?.deductionClassification || 'normal',
    specialDeduction: housingLoanDetail?.specialDeduction || 'none',
    yearEndBalance: amountFormat(housingLoanDetail?.yearEndBalance)
  };
};
